import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff, Sun, Moon, Globe } from 'lucide-react';

const t = (lang, de, en) => (lang === 'de' ? de : en);

const PartnerAuth = ({ mode = 'register' }) => {
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', social_media: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (!isLogin && !form.name)) {
      toast.error(t(language, 'Bitte alle Pflichtfelder ausfüllen.', 'Please fill in all required fields.'));
      return;
    }
    if (!isLogin && form.password.length < 6) {
      toast.error(t(language, 'Passwort muss mindestens 6 Zeichen haben.', 'Password must be at least 6 characters.'));
      return;
    }
    setIsSubmitting(true);
    try {
      const endpoint = isLogin ? '/api/partners/login' : '/api/partners/register';
      const body = isLogin
        ? { email: form.email, password: form.password }
        : { name: form.name, email: form.email, password: form.password, phone: form.phone, social_media: form.social_media };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');

      localStorage.setItem('partnerToken', data.token);
      localStorage.setItem('partnerData', JSON.stringify(data.partner));
      toast.success(isLogin
        ? t(language, 'Willkommen zurück!', 'Welcome back!')
        : t(language, 'Registrierung erfolgreich!', 'Registration successful!')
      );
      navigate('/partner/dashboard');
    } catch (err) {
      toast.error(err.message || t(language, 'Ein Fehler ist aufgetreten.', 'An error occurred.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center transition-colors duration-500 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
      {/* Controls */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <button onClick={toggleLanguage} className={`p-2.5 rounded-full backdrop-blur-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-gray-700'}`}><Globe className="w-4 h-4" /></button>
        <button onClick={toggleTheme} className={`p-2.5 rounded-full backdrop-blur-md transition-all ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-gray-700'}`}>{isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
      </div>

      <div className="w-full max-w-md mx-auto px-4 py-12">
        <Link to="/partner" className="block text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_dion-hair/artifacts/cm40t14d_DionHairClinic_Logo.svg" alt="Dion Hair Clinic" className={`h-12 w-auto mx-auto ${isDark ? 'brightness-0 invert' : ''}`} />
        </Link>

        <div className={`rounded-2xl p-6 sm:p-8 border ${isDark ? 'bg-[#111] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`}>
          <h1 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {isLogin ? t(language, 'Partner-Login', 'Partner Login') : t(language, 'Partner werden', 'Become a Partner')}
          </h1>
          <p className={`text-sm mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {isLogin ? t(language, 'Melde dich in deinem Dashboard an.', 'Log in to your dashboard.') : t(language, 'Erstelle dein kostenloses Partner-Konto.', 'Create your free partner account.')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-auth-form">
            {!isLogin && (
              <div>
                <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{t(language, 'Name', 'Name')} *</Label>
                <Input data-testid="partner-name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder={t(language, 'Dein Name oder Firmenname', 'Your name or company name')} className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
              </div>
            )}
            <div>
              <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{t(language, 'E-Mail', 'Email')} *</Label>
              <Input data-testid="partner-email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="partner@email.de" className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
            </div>
            <div>
              <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{t(language, 'Passwort', 'Password')} *</Label>
              <div className="relative mt-1">
                <Input data-testid="partner-password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} placeholder={isLogin ? '********' : t(language, 'Min. 6 Zeichen', 'Min. 6 characters')} className={`pr-10 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {!isLogin && (
              <>
                <div>
                  <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{t(language, 'Telefon (optional)', 'Phone (optional)')}</Label>
                  <Input data-testid="partner-phone" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+49..." className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
                </div>
                <div>
                  <Label className={isDark ? 'text-white/70' : 'text-gray-600'}>{t(language, 'Social Media (optional)', 'Social Media (optional)')}</Label>
                  <Input data-testid="partner-social" value={form.social_media} onChange={(e) => setForm(p => ({ ...p, social_media: e.target.value }))} placeholder="@instagram_handle" className={`mt-1 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200'}`} />
                </div>
              </>
            )}

            <Button type="submit" disabled={isSubmitting} data-testid="partner-submit-btn" className="w-full py-3 h-auto bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white font-semibold text-base shadow-lg shadow-[#7BA7C2]/20 transition-all hover:shadow-xl">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="60 30" /></svg>
                  {t(language, 'Bitte warten...', 'Please wait...')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? t(language, 'Anmelden', 'Log in') : t(language, 'Kostenlos registrieren', 'Register for free')}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => setIsLogin(!isLogin)} className={`text-sm hover:underline ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} data-testid="partner-toggle-auth">
              {isLogin ? t(language, 'Noch kein Konto? Jetzt registrieren', 'No account? Register now') : t(language, 'Bereits Partner? Jetzt anmelden', 'Already a partner? Log in')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerAuth;
